import React, { useState, useEffect, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import { useApiClient } from '../../api/ApiClientContext';
import { ProgrammeResponse } from '../../api/Api';
import layoutStyles from '../../styles/sidebarLayout.module.scss';
import styles from './AboutProgramPage.module.scss';
import { getHabitImageByID } from '../../utils/habitImages';
import { Icon } from '../../assets/icons/Icon';

type IconName = 'KeepTrackIcon' | 'DevelopBehaviours' | 'BlueArrowRight' | 'BringCulture';

const iconsContent: { title: string; text: string; iconName: IconName }[] = [
  {
    title: 'Keep track of your habit',
    text: 'Commit to selected habits at least 30 times / 60 days',
    iconName: 'KeepTrackIcon',
  },
  {
    title: 'Develop behaviours',
    text: 'By committing to habits and tracking your progress',
    iconName: 'DevelopBehaviours',
  },
  {
    title: 'Bring culture change',
    text: 'With science and psychology behind team habits',
    iconName: 'BringCulture',
  },
];

interface AboutInitiativeProps {
  programme?: ProgrammeResponse | null;
}

const AboutInitiative = ({ programme }: AboutInitiativeProps) => {
  if (!programme) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIconWrapper}>
          <Icon iconName="GreyCalendarIcon" width="5rem" height="5rem" />
        </div>
        <h3 className={styles.emptyTitle}>No active initiative</h3>
        <p className={styles.emptyText}>
          You do not have an active initiative at the moment. You will be notified when a new initiative is available.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.contentCard}>
      <img
        src={getHabitImageByID('program-intro')}
        className={styles.programImage}
        alt="Program"
      />

      <h2 className={styles.programTitle}>{programme?.short_description}</h2>

      <div className={styles.programStats}>
        <span className={styles.statItem}>{programme?.duration_days || 0} DAYS</span>
        <span className={styles.statItem}>{programme?.modules?.length || 0} MODULES</span>
        <span className={styles.statItem}>{programme?.behaviours_count || 0} BEHAVIOURS</span>
        <span className={styles.statItem}>{programme?.habits_count || 0} HABITS</span>
      </div>

      {programme?.description && (
        <div className={styles.programDescription}>
          <ReactMarkdown>{programme.description}</ReactMarkdown>
        </div>
      )}

      {programme?.intro_content && (
        <div className={styles.programDescription}>
          <ReactMarkdown>{programme.intro_content}</ReactMarkdown>
        </div>
      )}

    </div>
  );
};

const AboutHabiTrack = () => {
  return (
    <>
      <div className={styles.contentCard}>
        <div className={styles.iconsSection}>
          <div className={styles.iconsRow}>
            {iconsContent.map((item, idx) => (
              <Fragment key={item.title}>
                {idx > 0 && (
                  <Icon iconName="BlueArrowRight" width="1.3rem" height="1.4rem" />
                )}
                <div className={styles.iconItem}>
                  <Icon iconName={item.iconName} width="5.88rem" height="5.88rem" />
                  <div className={styles.iconTitle}>{item.title}</div>
                  <div className={styles.iconText}>{item.text}</div>
                </div>
              </Fragment>
            ))}
          </div>
        </div>

        <div className={styles.textSection}>
          <p className={styles.sectionDescription}>
            Habits support your personal development and learning journey. Keep track of your progress
            and endorse your teammates effort. You can also see what your team are working towards and
            their individual and collective progress.
          </p>
          <p className={styles.sectionDescription}>
            Through facilitated modules, coaching sessions, and peer learning experiences, you'll explore what empathy looks like
            in real leadership moments - from showing vulnerability and giving feedback, to managing conflict and building
            inclusive teams. Your HabiTrack™ dashboard is where these ideas turn into practice. Grounded in behavioural science,
            HabiTrack™ helps you develop behaviours and drives organisational culture change.
          </p>
        </div>
      </div>

      <div className={styles.textSection}>
        <h2 className={styles.sectionTitle}>Science and psychology of habit formation</h2>
        <p className={styles.sectionDescription}>
          Habits support your personal development and learning journey. Keep track of your progress
          and endorse your teammates effort. You can also see what your team are working towards and
          their individual and collective progress.
        </p>
        <p className={styles.sectionDescription}>
          Through facilitated modules, coaching sessions, and peer learning experiences, you'll explore what empathy looks like
          in real leadership moments - from showing vulnerability and giving feedback, to managing conflict and building
          inclusive teams. Your HabiTrack™ dashboard is where these ideas turn into practice. Grounded in behavioural science,
          HabiTrack™ helps you develop behaviours and drives organisational culture change.
        </p>
      </div>
    </>
  );
};

const AboutProgramPage = () => {
  const [activeTab, setActiveTab] = useState<'initiative' | 'habitrack'>('initiative');
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const apiClient = useApiClient();

  const { data: programmes } = useQuery({
    queryKey: ['programmes'],
    queryFn: async () => {
      const res = await apiClient.api.getAvailableProgrammesApiV1ProgrammesGet();
      return res?.data;
    },
  });

  // Auto-select first programme when data loads
  useEffect(() => {
    if (programmes?.length && !selectedProgramId) {
      setSelectedProgramId(programmes[0].id);
    }
  }, [programmes, selectedProgramId]);

  const selectedProgramme = programmes?.find(p => p.id === selectedProgramId) || null;

  const programOptions = (programmes || []).map(p => ({
    value: p.id,
    label: p.name || 'Initiative',
  }));

  return (
    <div className={layoutStyles.page}>
      <div className={layoutStyles.pageContainer}>
        {/* Sidebar with vertical tabs */}
        <aside className={layoutStyles.sidebar}>
          <nav className={layoutStyles.sidebarNav}>
            <button
              className={`${layoutStyles.sidebarTab} ${activeTab === 'initiative' ? layoutStyles.active : ''}`}
              onClick={() => setActiveTab('initiative')}
            >
              About initiative
            </button>
            <button
              className={`${layoutStyles.sidebarTab} ${activeTab === 'habitrack' ? layoutStyles.active : ''}`}
              onClick={() => setActiveTab('habitrack')}
            >
              About HabiTrack
            </button>
          </nav>
        </aside>

        {/* Main content */}
        <main className={layoutStyles.mainContent}>
          <div className={styles.pageTitleRow}>
            <h1 className={layoutStyles.pageTitle}>
              {activeTab === 'initiative' ? 'About initiative' : 'About HabiTrack'}
            </h1>
            {activeTab === 'initiative' && (
              <Select
                data={programOptions}
                value={selectedProgramId}
                onChange={setSelectedProgramId}
                placeholder="Select initiative"
                leftSection={<Icon iconName="SelectUserIcon" width="1.25rem" height="1.25rem" />}
                rightSection={<Icon iconName="ArrowDownIcon" width="0.75rem" height="0.75rem" />}
                classNames={{
                  input: styles.selectInput,
                  dropdown: styles.selectDropdown,
                  root: styles.selectRoot,
                  wrapper: styles.selectWrapper,
                }}
                allowDeselect={false}
              />
            )}
          </div>
          <div className={layoutStyles.tabContent}>
            {activeTab === 'initiative' && <AboutInitiative programme={selectedProgramme} />}
            {activeTab === 'habitrack' && <AboutHabiTrack />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AboutProgramPage;
